import React, { useEffect } from 'react';
import { WagmiProvider } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RainbowKitProvider, darkTheme, lightTheme } from '@rainbow-me/rainbowkit';
import { useAccount, useConnect, useBalance, useChainId } from 'wagmi';
import { wagmiConfig } from '../../config/wagmi';
import { useWalletStore } from '../../stores/walletStore';
import { useAppStore } from '../../stores/appStore';
import '@rainbow-me/rainbowkit/styles.css';

// Create a stable query client instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes
    },
  },
});

// Inner component that uses wagmi hooks
function WalletSync() {
  const { address, isConnected, connector } = useAccount();
  const chainId = useChainId();
  const { data: balance } = useBalance({ address });
  const { setAccount, setBalance, setConnecting } = useWalletStore();
  const { isPending: isConnecting } = useConnect();

  // Sync wallet state with our store
  useEffect(() => {
    if (isConnected && address) {
      setAccount({
        address,
        chainId,
        isConnected: true,
        connector: connector?.name,
      });
    } else {
      setAccount(null);
    }
  }, [isConnected, address, chainId, connector, setAccount]);

  // Sync balance
  useEffect(() => {
    if (balance) {
      setBalance(balance.formatted);
    } else {
      setBalance('0');
    }
  }, [balance, setBalance]);

  // Sync connecting state
  useEffect(() => {
    setConnecting(isConnecting);
  }, [isConnecting, setConnecting]);

  return null;
}

interface WalletProviderProps {
  children: React.ReactNode;
}

export function WalletProvider({ children }: WalletProviderProps) {
  const { theme } = useAppStore();

  // Configure RainbowKit theme based on app theme
  const rainbowKitTheme = theme === 'dark' 
    ? darkTheme({
        accentColor: '#3b82f6', // blue-500
        accentColorForeground: 'white',
        borderRadius: 'medium',
      })
    : lightTheme({
        accentColor: '#3b82f6', // blue-500
        accentColorForeground: 'white', 
        borderRadius: 'medium',
      });

  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <RainbowKitProvider
          theme={rainbowKitTheme}
          showRecentTransactions={true}
          appInfo={{
            appName: 'Encrypt ERC v2',
            disclaimer: ({ Text, Link }) => (
              <Text>
                By connecting your wallet, you agree to the{' '}
                <Link href="/terms">Terms of Service</Link> and acknowledge you have read and understand the protocol{' '}
                <Link href="/disclaimer">Disclaimer</Link>.
              </Text>
            ),
          }}
        >
          <WalletSync />
          {children}
        </RainbowKitProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
}